// src/pages/accounting/general/GeneralSalesForm.jsx
import React, { useState, useEffect, useRef } from "react";
import styles from "./GeneralSalesForm.module.css";
import DepositInfoSection from "../../../components/DepositInfoSection";
import api from "../../../api";

export default function GeneralSalesForm() {

  // ✅ 페이지 타이틀
  useEffect(() => {
    document.title = "(일반) 매출자료 작성";
  }, []); // eslint-disable-line

  // ✅ 페이지 로드 시 마우스커서 사업입력 창에 포커스
  useEffect(() => {
    businessInputRef.current?.focus();
  }, []);

  // ✅ 기본 입력 데이터
  const [form, setForm] = useState({
    draft_date: new Date().toISOString().slice(0, 10),
    customer_name: "",
  
    vendor_name: "",   // ⭐ 추가
    vendor_id: null,   // ⭐ 추가
    business_number: "", // ⭐ 선택
  
    description: "",
    amount: "",
    pay_method: "transfer",
    deposit_amount: "",
    deposit_date: "",
    deposit_method: "transfer",
    bank_account: "",
    account_subject: "",
    note: "",
  });

  const [loadedFromDraft, setLoadedFromDraft] = useState(false);
  const [originalDescription, setOriginalDescription] = useState("");  // 👈 추가



  // ✅ 조회조건(왼쪽 필터)
  const [year, setYear] = useState(new Date().getFullYear());
  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate, setToDate] = useState(`${year}-12-31`);
  useEffect(() => {
    setFromDate(`${year}-01-01`);
    setToDate(`${year}-12-31`);
  }, [year]);

  const businessOptions = [
    { label: "경영팀", code: 1 },
    { label: "용역사업", code: 2 },
    { label: "CAFE_INN 가산점", code: 3 },
    { label: "CAFE_INN 독산점", code: 4 },
    { label: "CAFE_INN 금나래점", code: 5 },
    { label: "환경/소독", code: 6 },
    { label: "환경/종합", code: 7 },
  ];

  // ✅ 1. 체크박스 UI 유지, 단일 선택 동작
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [inputBusinessCode, setInputBusinessCode] = useState("");
  const [draftNos, setDraftNos] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState("");
  const businessInputRef = useRef(null);  // 페이지 로드 시 왼쪽패널 사업분야 입력창으로 이동
  const draftSelectRef = useRef(null);
  const radioDirectRef = useRef(null);
  const depositDateRef = useRef(null);

  // ✅ 기안번호 직접입력 관련 상태
  const [draftInputMode, setDraftInputMode] = useState("direct");
  const [draftSearchText, setDraftSearchText] = useState("");
  const [filteredDrafts, setFilteredDrafts] = useState([]);
  const [showDraftDropdown, setShowDraftDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const skipDropdownRef = useRef(false);  // ⭐ 플래그 추가

  // ✅ 통장 목록 상태 및 로딩
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        console.debug("[통장] 목록 불러오기 시작");
        const res = await api.get("bank-accounts/");
        const rows = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setBankAccounts(rows);
        console.debug("[통장] 로드 완료:", rows.length, "건");
      } catch (error) {
        console.error("❌ 통장 목록 불러오기 실패:", error);
      }
    })();
  }, []);

  // ✅ 숫자 포맷 (천단위 콤마)
  const formatNumberWithComma = (value) => {
    if (!value) return "";
    const numeric = value.toString().replace(/[^0-9]/g, "");
    return numeric.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // ✅ 입력 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    console.debug(`[입력변경] ${name} ->`, value);
    if (name === "amount" || name === "deposit_amount") {
      const formatted = formatNumberWithComma(value);
      setForm((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ✅ 1. 체크박스 토글 - 마지막 클릭만 체크
  const handleBusinessToggle = async (optionLabel) => {
    if (selectedBusinesses.includes(optionLabel)) {
      setSelectedBusinesses([]);
      setShowList(false);
    } else {
      setSelectedBusinesses([optionLabel]);
      await fetchDraftNos(optionLabel);
    }
  };

  const [draftStatus, setDraftStatus] = useState([]);
  const [showList, setShowList] = useState(false);
  const [showFilter, setShowFilter] = useState("all");
  const [accountSearch, setAccountSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  // ====================== 기안번호 불러오기 ==========================
  const fetchDraftNos = async (categoryLabel) => {
    try {
      console.debug(`[기안] ${categoryLabel} 번호 목록 로딩 시작`);

      const mapping = {
        "경영팀": { category: "경영팀", subcategory: "" },
        "용역사업": { category: "용역사업", subcategory: "" },
        "CAFE_INN 가산점": { category: "CAFE_INN", subcategory: "가산점" },
        "CAFE_INN 독산점": { category: "CAFE_INN", subcategory: "독산점" },
        "CAFE_INN 금나래점": { category: "CAFE_INN", subcategory: "금나래점" },
        "환경/소독": { category: "환경/소독", subcategory: "" },
        "환경/종합": { category: "환경/종합", subcategory: "" },
      };

      const params = mapping[categoryLabel];
      if (!params) return console.warn("⚠️ 지원되지 않는 사업입니다:", categoryLabel);

      let endpoint = "";
      switch (categoryLabel) {
        case "경영팀":
          endpoint = "income/business/";
          break;
        case "용역사업":
          endpoint = "income-service/";
          break;
        case "CAFE_INN 가산점":
          endpoint = "income/cafe/gasan/";
          break;
        case "CAFE_INN 독산점":
          endpoint = "income/cafe/doksan/";
          break;
        case "CAFE_INN 금나래점":
          endpoint = "income/cafe/geumnarae/";
          break;
        case "환경/소독":
          endpoint = "income-drafts/";
          break;
        case "환경/종합":
          endpoint = "env-total-incomes/";
          break;
      }

      const res = await api.get(endpoint, {
        params: { 
          ...params, 
          ordering: "draft_date,seq",  // 오름차순 (오래된 것 먼저)
          draft_date__gte: fromDate,
          draft_date__lte: toDate
        },
      });
      

      const data = Array.isArray(res.data) ? res.data : res.data.results || [];

      // ✅ 프론트엔드에서 날짜 필터링 추가
      const filteredData = data.filter((item) => {
        if (!item.draft_date) return false;
        return item.draft_date >= fromDate && item.draft_date <= toDate;
      });

      // 🔥 여기 추가: 오름차순 정렬 강제 적용
      filteredData.sort((a, b) => {
        if (a.draft_date < b.draft_date) return -1;
        if (a.draft_date > b.draft_date) return 1;
        if (a.seq < b.seq) return -1;
        if (a.seq > b.seq) return 1;
        return 0;
      });
      
      const draftList = filteredData.map((item) => ({  // ✅ data → filteredData
        draft_no: item.draft_no,
        partner: item.partner?.name ?? item.partner_name ?? "",
      }));

      const checkRes = await api.get("general-sales/");
      const existing = Array.isArray(checkRes.data) ? checkRes.data : checkRes.data.results || [];

      const status = draftList.map((d) => ({
        draft_no: d.draft_no,
        partner: d.partner,
        exists: existing.some((g) => g.draft_no === d.draft_no),
      }));

      setDraftStatus(status);
      setShowList(true);

      const notWritten = status.filter((s) => !s.exists).map((s) => s.draft_no);
      setDraftNos(notWritten);

      console.debug("[기안] 번호/작성여부 로드 완료:", status.length, "건");
    } catch (err) {
      console.error("❌ 기안번호/작성여부 불러오기 실패:", err);
    }
  };

  // ✅ 날짜 변경 시 선택된 사업이 있으면 자동 재조회 추가!
  useEffect(() => {
    if (selectedBusinesses.length > 0) {
      fetchDraftNos(selectedBusinesses[0]);
    }
  }, [fromDate, toDate]); // eslint-disable-line

  // ✅ 계정과목 목록 상태 및 로딩
  const [accountSubjects, setAccountSubjects] = useState([]);

  useEffect(() => {
    async function fetchSubjects() {
      try {
        const res = await api.get("general-sales-accounts/");
        const rows = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setAccountSubjects(rows);
        console.debug("[계정과목] 로드 완료:", rows.length, "건");
      } catch (err) {
        console.error("❌ 계정과목 불러오기 실패:", err);
      }
    }
    fetchSubjects();
  }, []);

  // ✅ 자동검색 useEffect
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!accountSearch.trim()) {
        setSuggestions([]);
        return;
      }
      try {
        const res = await api.get(`general-sales-accounts/?search=${accountSearch}`);
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setSuggestions(data);
      } catch (err) {
        console.error("❌ 계정과목 자동완성 실패:", err);
      }
    };

    const timer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timer);
  }, [accountSearch]);

  // ✅ 3. 직접입력 검색어에 따라 기안번호 필터링 + 드롭다운 표시
  useEffect(() => {
    // ⭐ Enter로 선택한 경우 드롭다운을 다시 열지 않음
    if (skipDropdownRef.current) {
      return;
    }

    if (draftSearchText.trim() === "") {
      setFilteredDrafts([]);
      setShowDraftDropdown(false);
    } else {
      const filtered = draftNos.filter((no) =>
        no.includes(draftSearchText)
      );
      setFilteredDrafts(filtered);
      setShowDraftDropdown(filtered.length > 0);
      setHighlightIndex(0);
    }
  }, [draftSearchText, draftNos]);

  // ✅ 3. 키보드 탐색 (화살표, Enter, Escape) - 수정됨
  const handleDraftKeyDown = (e) => {
    if (!showDraftDropdown || filteredDrafts.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredDrafts.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredDrafts.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredDrafts[highlightIndex]) {
        const selected = filteredDrafts[highlightIndex];
        
        // ⭐ 플래그 설정 - useEffect가 드롭다운을 다시 열지 못하도록
        skipDropdownRef.current = true;
        
        // 상태 업데이트
        setSelectedDraft(selected);
        setDraftSearchText(selected);
        setFilteredDrafts([]);
        setShowDraftDropdown(false);
        
        // 데이터 불러오기
        handleLoadData(selected);
        
        // ⭐ 플래그 리셋 (다음 검색을 위해)
        setTimeout(() => {
          skipDropdownRef.current = false;
        }, 100);
      }
    } else if (e.key === "Escape") {
      setShowDraftDropdown(false);
      setFilteredDrafts([]);
    }
  };

  // ✅ 3. 드롭다운에서 기안번호 선택
  const selectDraftFromDropdown = (draftNo) => {
    skipDropdownRef.current = true;
    setSelectedDraft(draftNo);
    setDraftSearchText(draftNo);
    setShowDraftDropdown(false);
    setTimeout(() => {
      skipDropdownRef.current = false;
    }, 100);
  };

  // ✅ 선택된 기안번호로 기존 데이터 불러오기
  const handleLoadData = async (draftNo = null) => {
    const targetDraft = draftNo || selectedDraft;
    
    if (!targetDraft) {
      alert("기안번호를 선택해주세요.");
      return;
    }

    // ✅ 선택된 사업이 없으면 알림
    if (selectedBusinesses.length === 0) {
      alert("사업을 먼저 선택해주세요.");
      return;
    }

    try {
      console.debug("[기안] 불러오기:", targetDraft);
      const encoded = encodeURIComponent(targetDraft);
      
      // ✅ 선택된 사업에 따라 적절한 엔드포인트 결정
      const businessLabel = selectedBusinesses[0];
      let endpoint = "";
      
      switch (businessLabel) {
        case "경영팀":
          endpoint = `income/business/by-no/${encoded}/`;
          break;
        case "용역사업":
          endpoint = `income-service/by-no/${encoded}/`;
          break;
        case "CAFE_INN 가산점":
          endpoint = `income/cafe/gasan/by-no/${encoded}/`;
          break;
        case "CAFE_INN 독산점":
          endpoint = `income/cafe/doksan/by-no/${encoded}/`;
          break;
        case "CAFE_INN 금나래점":
          endpoint = `income/cafe/geumnarae/by-no/${encoded}/`;
          break;
        case "환경/소독":
          endpoint = `income-drafts/by-no/${encoded}/`;
          break;
        case "환경/종합":
          endpoint = `env-total-incomes/by-no/${encoded}/`;
          break;
          
        default:
          alert("지원되지 않는 사업입니다.");
          return;
      }

      console.debug("[기안] 엔드포인트:", endpoint);
      const res = await api.get(endpoint);  // ⭐ 사업별 올바른 엔드포인트 사용
      const d = res.data;

      setForm({
        draft_date: d.draft_date || new Date().toISOString().slice(0, 10),
        customer_name: d.partner?.name ?? d.partner_name ?? "",

        vendor_name: d.partner?.name ?? d.partner_name ?? "",   // ⭐ 추가
        vendor_id: d.partner?.id ?? null,                       // ⭐ 추가
        business_number: d.partner?.business_number ?? "",      // ⭐ 선택

        description: d.title || d.description || "",
        amount: formatNumberWithComma(d.amount ?? ""),

        pay_method: d.pay_method ?? "transfer",
        deposit_amount: formatNumberWithComma(d.amount ?? ""),
        deposit_date: d.draft_date ?? "",
        deposit_method: d.pay_method ?? "transfer",
        bank_account: "",
        account_subject: d.account_subject?.id || "",
        note: d.desc || "",
      });

      setLoadedFromDraft(true);
      alert("✅ 기안 자료가 불러와졌습니다!");
      
      setTimeout(() => {
        depositDateRef.current?.focus();
      }, 100);
    } catch (err) {
      console.error("❌ 기안자료 불러오기 실패:", err);
      alert("❌ 기안자료를 불러오지 못했습니다.");
    }
  };
  


  // ✅ 숫자 입력 → 사업 자동 선택
  const handleBusinessKey = async (e) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      const num = parseInt(inputBusinessCode, 10);
      const found = businessOptions.find((b) => b.code === num);

      if (found) {
        setSelectedBusinesses([found.label]);
        await fetchDraftNos(found.label);
      
        // ⭐ 미작성 필터 자동 선택
        setShowFilter("not");

        // ⭐ 기안번호 직접입력으로 포커스 이동
        setTimeout(() => {
          radioDirectRef.current?.focus();
        }, 200);
      } else {
        alert("해당 번호의 사업구분이 없습니다.");
      }
      setInputBusinessCode("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        draft_no: selectedDraft || "",
        draft_date: form.draft_date,
        deposit_date: form.deposit_date || null,
      
        // 💡 기안 거래처와 실제 거래처 모두 저장
        customer_name: form.customer_name || "",   // 기안 거래처명
        vendor_name: form.vendor_name || "",       // 실제 선택한 거래처명
        vendor_id: form.vendor_id || null,
        business_number: form.business_number || "",
      
        // 💡 설명
        description: form.description,
      
        // 💡 계산 항목
        supply_amount: Number(form.supplyValue?.replace(/,/g, "")) || 0,
        vat_amount: Number(form.vatValue?.replace(/,/g, "")) || 0,
        fee_amount: Number(form.feeValue?.replace(/,/g, "")) || 0,
        total_amount: Number(form.totalValue?.replace(/,/g, "")) || 0,
      
        // 💡 결제방법 / 결제유형
        pay_method: form.deposit_method,       // transfer / card / cash
        pay_type: form.tax_type || "",         // 과세 / 면세 / 카드과세 / 카드면세
      
        // 💡 입금 통장
        bank_account: Number(form.bank_account) || null,
        account_subject: Number(form.account_subject) || null,
      
        // 💡 비목 / 세목 / 증빙유형
        bimok: form.bimok || "",
        semok: form.semok || "",
        doc_type: form.evidence_type || "",    // 계산서 / 세금계산서 / 카드전표 등
      
        // 💡 비고
        note: form.note || "",
      };
      
      // 🔍 디버깅 로그 추가
      console.debug("======= [저장요청 Payload] =======");
      Object.entries(payload).forEach(([k, v]) => console.debug(`${k}:`, v));
      console.debug("=================================");
  
      console.debug("[저장] payload:", payload);
  
      const res = await api.post("general-sales/", payload);
      alert("✅ 저장되었습니다!");
      console.debug("[저장 완료]", res.data);
  
    } catch (err) {
      console.error("❌ 저장 오류:", err.response?.data || err);
      alert("❌ 저장 중 오류가 발생했습니다.");
    }
  };
  

  // ✅ UI
  return (
    <div className={styles.mainLayout}>
      {/* 1️⃣ 조회조건 */}
      <div className={styles.filterArea}>
        <h3>조회조건</h3>

        <div className={styles.filterGroup}>
          <label>연도 선택</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
          />
        </div>

        <div className={styles.filterGroup}>
          <label>기간 선택</label>
          <div className={styles.dateColumn}>
            <div>
              <span>시작일자</span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <span>종료일자</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.filterGroup}>
          <label>사업 구분</label>
          <input
            ref={businessInputRef}  // 페이지 로드 시 마우스 커서 입력창 위치
            type="text"
            value={inputBusinessCode}
            onChange={(e) => setInputBusinessCode(e.target.value)}
            onKeyDown={handleBusinessKey}
            placeholder="번호 입력 후 Enter"
          />
          <div className={styles.businessGroup}>
            {businessOptions.map((opt) => (
              <label key={opt.code} className={styles.businessLabel}>
                <input
                  type="checkbox"
                  checked={selectedBusinesses.includes(opt.label)}
                  onChange={() => handleBusinessToggle(opt.label)}
                  className={styles.businessCheckbox}
                />
                {opt.label} ({opt.code})
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* 2️⃣ 가운데: (일반) 매출자료 작성 여부 */}
      <div className={styles.centerPanel}>
        <div className={styles.stickyHeader}>
          <h3>(일반) 매출자료 작성 여부</h3>

          <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
            <button
              type="button"
              onClick={() => setShowFilter("all")}
              style={{
                background: showFilter === "all" ? "#2563eb" : "#e5e7eb",
                color: showFilter === "all" ? "white" : "#374151",
                padding: "4px 10px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
              }}
            >
              전체
            </button>

            <button
              type="button"
              onClick={() => setShowFilter("done")}
              style={{
                background: showFilter === "done" ? "#16a34a" : "#e5e7eb",
                color: showFilter === "done" ? "white" : "#374151",
                padding: "4px 10px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
              }}
            >
              작성됨 ✅
            </button>

            <button
              type="button"
              onClick={() => setShowFilter("not")}
              style={{
                background: showFilter === "not" ? "#dc2626" : "#e5e7eb",
                color: showFilter === "not" ? "white" : "#374151",
                padding: "4px 10px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
              }}
            >
              미작성 ❌
            </button>
          </div>
        </div>

        {selectedBusinesses.length === 0 && (
          <p
            style={{
              textAlign: "center",
              marginTop: "40px",
              color: "#6b7280",
            }}
          >
            사업을 선택하세요
          </p>
        )}

        {selectedBusinesses.length > 0 && showList && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>기안번호</th>
                <th>거래처</th>
                <th>작성여부</th>
              </tr>
            </thead>
            <tbody>
              {draftStatus
                .filter((d) => {
                  if (showFilter === "done") return d.exists;
                  if (showFilter === "not") return !d.exists;
                  return true;
                })
                .map((d, idx) => (
                  <tr key={idx}>
                    <td style={{ whiteSpace: "pre-line", lineHeight: "1.4em" }}>
                      {d.draft_no.replace("-", "\n-")}
                    </td>
                    <td>{d.partner}</td>
                    <td>{d.exists ? "✅" : "❌"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 3️⃣ 오른쪽: 매출자료 입력 */}
      <form onSubmit={handleSubmit} className={styles.rightPanel}>
        <h3>매출자료 입력</h3>

        {/* ✅ 기안번호 선택 */}
        {[
          "경영팀",
          "용역사업",
          "환경/소독",
          "환경/종합",
          "CAFE_INN 가산점",
          "CAFE_INN 독산점",
          "CAFE_INN 금나래점"
        ].some(b => selectedBusinesses.includes(b)) && (
          <div className={styles.centerDraftBox}>
            
            <div className={styles.draftHeaderRow}>
            <label className={styles.draftLabel}>기안번호 선택</label>

            <div className={styles.inputModeSelectorInline}>
              <label className={styles.radioLabel}>
                <input
                  ref={radioDirectRef}
                  type="radio"
                  value="direct"
                  checked={draftInputMode === "direct"}
                  onChange={(e) => setDraftInputMode(e.target.value)}
                />
                직접입력
              </label>
              <label className={styles.radioLabel}>
                <input type="radio" value="dropdown" checked={draftInputMode === "dropdown"} onChange={(e) => setDraftInputMode(e.target.value)} />
                목록선택
              </label>
            </div>
            </div>

            <div className={styles.draftSelectorRow}>
              {draftInputMode === "direct" && (
                <div style={{ position: "relative", flex: 1 }}>
                  <input
                    type="text"
                    placeholder="기안번호 입력 (예: 251031)"
                    value={draftSearchText}
                    onChange={(e) => setDraftSearchText(e.target.value)}
                    onFocus={() => {
                      if (filteredDrafts.length > 0) {
                        setShowDraftDropdown(true);
                      }
                    }}
                    onKeyDown={handleDraftKeyDown}
                    className={styles.draftSearchInput}
                  />

                  {showDraftDropdown && (
                    <div className={styles.draftDropdown}>
                      {filteredDrafts.length > 0 ? (
                        filteredDrafts.map((no, idx) => (
                          <div
                            key={idx}
                            className={`${styles.draftItem} ${
                              idx === highlightIndex ? styles.activeDraftItem : ""
                            }`}
                            onClick={() => selectDraftFromDropdown(no)}
                          >
                            {no}
                          </div>
                        ))
                      ) : (
                        <div className={styles.draftItem}>일치하는 기안번호 없음</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {draftInputMode === "dropdown" && (
                <select
                  ref={draftSelectRef}
                  value={selectedDraft}
                  onChange={(e) => setSelectedDraft(e.target.value)}
                  className={styles.draftSelect}
                >
                  <option value="">기안 번호 선택</option>
                  {draftNos.map((no, idx) => (
                    <option key={idx} value={no}>
                      {no}
                    </option>
                  ))}
                </select>
              )}

              <button
                type="button"
                onClick={() => handleLoadData()}

                className={styles.loadButton}
              >
                자료 불러오기
              </button>
            </div>

            {draftInputMode === "direct" && draftSearchText && (
              <p className={styles.filterResultText}>
                {filteredDrafts.length}개의 기안번호가 검색되었습니다.
              </p>
            )}
          </div>
        )}

        {/* 상단: 기안 불러온 값 표시 */}
        <div className={styles.draftBox}>
          <div className={styles.rowFourCol}>
            <div className="field">
              <label>작성일(작업일)</label>
              <input
                type="date"
                name="draft_date"
                value={form.draft_date}
                onChange={handleChange}
                className={loadedFromDraft ? styles.dbField : ""}
              />
            </div>

            <div className="field">
              <label>거래처명</label>
              <input
                type="text"
                name="customer_name"
                value={form.customer_name}
                onChange={handleChange}
                className={loadedFromDraft ? styles.dbField : ""}
              />
            </div>

            <div className="field">
              <label>금액</label>
              <input
                type="text"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                className={loadedFromDraft ? styles.dbField : ""}
              />
            </div>

            <div className="field">
              <label>결제방법</label>
              <select
                name="pay_method"
                value={form.pay_method}
                onChange={handleChange}
                className={loadedFromDraft ? styles.dbField : ""}
              >
                <option value="transfer">계좌이체</option>
                <option value="card">카드결제</option>
                <option value="cash">현금결제</option>
              </select>
            </div>
          </div>

          <div className={styles.rowFull}>
            <label>적요</label>
            <input
              type="text"
              name="description"
              value={originalDescription || form.description}  // 👈 수정
              onChange={handleChange}
              className={loadedFromDraft ? styles.dbField : ""}
              readOnly={loadedFromDraft}  // 👈 추가 (선택사항: 읽기전용으로 만들기)

            />
          </div>
        </div>

        <hr className={styles.divider} />

        <DepositInfoSection
          form={form}
          setForm={setForm}
          selectedBusiness={selectedBusinesses[0] || ""}
          bankAccounts={bankAccounts}
          accountSearch={accountSearch}
          setAccountSearch={setAccountSearch}
          suggestions={suggestions}
          setSuggestions={setSuggestions}
          loadedFromDraft={loadedFromDraft}
          handleSubmit={handleSubmit}
          depositDateRef={depositDateRef}
          setParentOriginalDescription={setOriginalDescription}  // 👈 추가

        />

        <div className={styles.row}>
          <label>비고</label>
          <textarea
            name="note"
            value={form.note}
            onChange={handleChange}
            className={loadedFromDraft ? styles.dbField : ""}
          ></textarea>
        </div>
      </form>
    </div>
  );
}